import { Edit } from "@mui/icons-material"
import { Box, MenuItem, Select, TableRow, Tooltip } from "@mui/material"
import React, { useState } from "react"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { NexCheckbox } from "../../components/Fields/Checkbox"
import { NexTextField } from "../../components/NexTextField"
import { NexTableCell, NexTableOverlayCell } from "../../components/Table/NexTableCell"
import { Tool } from "../../components/Tool/Tool"
import { FlightlineEvent } from "../../models/FlightlineEvent"
import { EventSensorDetails, operatorLineStatusOptions } from "../../store/types/MissionsTypes"
import {
    EventPlannedLine,
    isAutocompletePlannedLineOptionDisabled,
    unplannedLineValue,
} from "../../utils/FlightlineEventUtils"
import { StringUtils } from "../../utils/StringUtils"
import { getSelectedRowsWithShift, handleTextfieldPaste } from "../../utils/TableUtils"
import { TimeUtilsDateFns } from "../../utils/TimeUtils"
import { convertLengthUnits } from "../../utils/UnitCalculations"
import { useCollectionEventEditorStore } from "../../zstore/CollectionEventEditorStore"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"

interface FlightlineTableRowProps {
    event: FlightlineEvent
}

export const FlightlineTableRow = ({ event }: FlightlineTableRowProps) => {
    const fe_id = event.fe_event_id

    const prefDist = useLocalStorageStore((state) => state.preferredDistanceUnit)
    const prefHeight = useLocalStorageStore((state) => state.preferredHeightUnit)
    const flightlineEvents = useFlightlineEventStore((state) => state.flightlineEvents)
    const selectedEventIds = useFlightlineEventStore((state) => state.selectedEventIds)
    const eventPlannedLines = useFlightlineEventStore((state) => state.eventPlannedLines)
    const lastSelectedEventId = useFlightlineEventStore((state) => state.lastSelectedEventId)
    const updateFlightlineEvents = useFlightlineEventStore((state) => state.updateFlightlineEvents)
    const updateSelectedEventIds = useFlightlineEventStore((state) => state.updateSelectedEventIds)
    const updateLastSelectedEventId = useFlightlineEventStore((state) => state.updateLastSelectedEventId)

    const setCollectionEvent = useCollectionEventEditorStore((state) => state.setValuesFromFlightline)

    const [showOverlay, setShowOverlay] = useState(false)

    const show = () => setShowOverlay(true)
    const hide = () => setShowOverlay(false)

    const curEventPlannedLabels = eventPlannedLines.filter((opt) =>
        event.fe_planned_line_ids.find((pl_id) => pl_id === opt.plannedLineId),
    )
    const curFlightPlanFromPlannedLines: number | undefined = curEventPlannedLabels[0]?.flightPlanId

    const sensorDetails = event.sensor_details ?? ""

    const handlePlannedLineChange = (newValue: EventPlannedLine[]) => {
        const eventPlannedLines = newValue.map((obj) => obj.plannedLineId)
        handleUpdate("fe_planned_line_ids", eventPlannedLines)
    }

    const handleUpdate = <T extends FlightlineEvent>(field: keyof T, value: T[keyof T]) => {
        const index = flightlineEvents.findIndex((event) => fe_id === event.fe_event_id)
        const row: FlightlineEvent = {
            ...flightlineEvents[index],
            [field]: value,
        }
        updateFlightlineEvents([...flightlineEvents.slice(0, index), row, ...flightlineEvents.slice(index + 1)])
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        const index = flightlineEvents.findIndex((event) => fe_id === event.fe_event_id)
        const [newEvents] = handleTextfieldPaste<FlightlineEvent>(e, index, "notes", flightlineEvents)
        updateFlightlineEvents(newEvents)
    }

    const handleSelect = (rowId: number, shiftClick: boolean) => {
        let updatedEventIds: number[]
        if (shiftClick) {
            // get all rows between the last row clicked and current clicked row
            const lastSelectedIndex = flightlineEvents.findIndex((fe) => fe.fe_event_id === lastSelectedEventId)
            const currentSelectedIndex = flightlineEvents.findIndex((fe) => fe.fe_event_id === rowId)

            const rowsFromShiftClick = getSelectedRowsWithShift(
                lastSelectedIndex,
                currentSelectedIndex,
                flightlineEvents,
            )
            const idsFromShiftClickRows = rowsFromShiftClick.map((row) => row.fe_event_id)
            if (selectedEventIds.includes(rowId)) {
                // deselect all
                updatedEventIds = selectedEventIds.filter((id) => !idsFromShiftClickRows.includes(id))
            } else {
                // select all
                updatedEventIds = [...new Set([...selectedEventIds, ...idsFromShiftClickRows])]
            }
        } else {
            if (selectedEventIds.includes(rowId)) {
                // deselect
                updatedEventIds = selectedEventIds.filter((id) => id !== rowId)
            } else {
                updatedEventIds = [...selectedEventIds.slice(), rowId]
            }
        }

        updateLastSelectedEventId(rowId)
        updateSelectedEventIds(updatedEventIds)
    }

    return (
        <TableRow
            sx={{ overflow: "hidden" }}
            //Show edit overlay
            onMouseEnter={show}
            onTouchStart={show}
            //Hide edit overlay
            onMouseLeave={hide}
            onTouchEnd={hide}
        >
            <NexTableCell sticky={true}>
                <NexTableCell>
                    <NexCheckbox
                        checked={selectedEventIds.includes(event.fe_event_id)}
                        onClick={(e) => handleSelect(event.fe_event_id, e.shiftKey)}
                    />
                </NexTableCell>
                <NexTableCell>
                    <NexAutocomplete
                        size="small"
                        multiple={true}
                        value={curEventPlannedLabels}
                        options={eventPlannedLines}
                        getOptionDisabled={(opt) =>
                            isAutocompletePlannedLineOptionDisabled(
                                opt,
                                event.fe_planned_line_ids,
                                curFlightPlanFromPlannedLines,
                            )
                        }
                        getOptionLabel={(opt) => opt.label}
                        isOptionEqualToValue={(opt, val) => opt.plannedLineId === val.plannedLineId}
                        onChange={(_, tableOption) => handlePlannedLineChange(tableOption)}
                        sx={{ minWidth: 260 }}
                        limitTags={1}
                        TextFieldProps={{
                            error: curEventPlannedLabels.length === 0,
                        }}
                    />
                </NexTableCell>
            </NexTableCell>
            <NexTableCell>{event.line_original_name}</NexTableCell>
            <NexTableCell>{event.coll_event_name}</NexTableCell>
            <NexTableCell>
                <Select
                    value={event.flight_status}
                    variant="outlined"
                    fullWidth
                    size="small"
                    MenuProps={{ sx: { maxHeight: 500 } }}
                    sx={{ minWidth: 180 }}
                    error={event.fe_planned_line_ids.includes(unplannedLineValue) && event.flight_status !== "Ignored"}
                    onChange={(e) => handleUpdate("flight_status", e.target.value)}
                >
                    {operatorLineStatusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </NexTableCell>
            <NexTableCell>
                <NexTextField
                    sx={{ minWidth: 220 }}
                    onPaste={handlePaste}
                    onChange={(value) => handleUpdate("notes", value)}
                    value={event.notes ?? ""}
                    size="small"
                    variant="outlined"
                />
            </NexTableCell>
            <NexTableCell>{getAcquiredNodesText(event, curEventPlannedLabels)}</NexTableCell>
            <NexTableCell>
                <Tooltip title={sensorDetails} disableFocusListener enterTouchDelay={0} leaveTouchDelay={5000}>
                    <Box maxWidth={200} overflow={"hidden"} textOverflow={"ellipsis"}>
                        {sensorDetails}
                    </Box>
                </Tooltip>
            </NexTableCell>
            <NexTableCell>
                {event.flight_line_start_time && TimeUtilsDateFns.formatTime(new Date(event.flight_line_start_time))}
            </NexTableCell>
            <NexTableCell>
                {event.flight_line_stop_time && TimeUtilsDateFns.formatTime(new Date(event.flight_line_stop_time))}
            </NexTableCell>
            <NexTableCell>{convertLengthUnits(event.flight_line_distance, prefDist)}</NexTableCell>
            <NexTableCell>{event.flight_line_duration}</NexTableCell>
            <NexTableCell>{convertLengthUnits(event.flown_agl, prefHeight)}</NexTableCell>
            <NexTableOverlayCell showOverlay={showOverlay}>
                <Tool
                    TooltipProps={{ placement: "left", disableInteractive: true }}
                    tooltip={"Manage Flightline"}
                    Icon={Edit}
                    onChange={() => setCollectionEvent(event)}
                />
            </NexTableOverlayCell>
        </TableRow>
    )
}

const getAcquiredNodesText = (event: FlightlineEvent, plannedLines: EventPlannedLine[]) => {
    const parsed = StringUtils.parseJSON<EventSensorDetails>(event.sensor_details ?? "")
    if (parsed?.images_planned != null && parsed.images_actual != null) {
        return `${parsed.images_actual} of ${parsed.images_planned}`
    }

    const imagesActual = event.node_events?.length
    const frameCounts = plannedLines.map((plannedLine) => plannedLine.frameCount)
    const imagesPlanned = frameCounts.every((frameCount): frameCount is number => frameCount != null)
        ? frameCounts.reduce((total, frameCount) => total + frameCount, 0)
        : undefined
    if (imagesActual != null && imagesPlanned != null && imagesPlanned > 0) {
        return `${imagesActual} of ${imagesPlanned}`
    }

    return "N/A"
}
